import { InventoryType } from './inventory-type.model'

export interface TransactionType {
  id: number
  name: TransactionTypeEnum
  transactionTypeKey: TransactionTypeKey
  code: string
}

export enum TransactionTypeEnum {
  CASH_TRANSFER = 'Cash Transfer',
  COUPON = 'Coupon',
  MANAGEMENT_FEE = 'Management Fee',
  CUSTODY_FEE = 'Custody Fee',
  MMF_PAYMENT = 'Money Market Funds',
  PAYDOWN = 'Principal Paydown',
  EQUITY_DIVIDEND = 'Equity Dividend',
  SECURITIES_LENDING_INCOME = 'Sec Lending Income',
  OVERDRAFT_CHARGE = 'Overdraft Charge',
  BUY = 'Buy',
  SELL = 'Sell',
  MATURITY = 'Maturity',
  REDEMPTION = 'Redemption',
  TRANSFER_IN = 'Transfer In',
  TRANSFER_OUT = 'Transfer Out',
  CALL_REDEMPTION = 'Call Redemption',
  PUT_REDEMPTION = 'Put Redemption',
  OTHER_INCOME = 'Other Income',
  FX_TRADE = 'Foreign Exchange Trade',
  OTHER_EXPENSE = 'Other Expense',
  FX_SPOT_TRADE = 'Spot FX Trade',
  FX_FORWARD_TRADE = 'Forward FX Trade',
  WRITE_DOWN = 'Writedown',
  CORPORATE_ACTION = 'Corporate Actions',
  EXCHANGE_IN = 'Exchange In',
  EXCHANGE_OUT = 'Exchange Out',
  CORPORATE_ACTION_SELL = 'Corporate Action Sell',
  INITIAL_LOAD = 'Initial Load',
  CAPITALIZATION_OF_INTEREST = 'Capitalization of Interest',
  EXPIRATION = 'Expiration',
  OPTION_SETTLE = 'Option Settle',
  OPTION_WRITE = 'Option Write',
  BUY_COVER = 'Buy Cover',
  SHORT_SELL = 'Short Sell',
  MARGIN_VARIATION_IN = 'Margin Variation In',
  MARGIN_VARIATION_OUT = 'Margin Variation Out',
  FUTURE_SETTLE = 'Future Settle',
  RETURN_OF_CAPITAL = 'Return of Capital',
  CAPITAL_GAIN_DISTRIBUTION = 'Capital Gain Distribution',
  CAPITAL_CONTRIBUTION = 'Capital Contribution',
  FOREIGN_TAX_WITHHELD = 'Foreign Tax Withheld',
  INITIAL_LOAD_OF_DEFERRED = 'Initial Load of Deferred',
  CONTRIBUTION_WITHDRAWAL = 'Cash Contribution / Withdrawal',
  CASH_FROM_CORPORATE_ACTION = 'Cash from Corporate Action',
  CASH_RECEIVABLE_ON_LOAD = 'Cash Receivable on Load',
  DIRECT_CASH_TRANSFER = 'Wire Transfer',
  STOCK_SPLIT = 'Stock Split',
  CURRENCY_FORWARD = 'Currency Forward',
  PRINCIPAL_REPAYMENT = 'Principal Repayment',
  OPTION_EXERCISE = 'Option Exercise',
  PREPAYMENT_ACCELERATION_PREMIUM = 'Prepayment / Acceleration Premium',
  MUTUAL_FUND_DIVIDEND = 'Mutual Fund Dividend',
  FOREIGN_TAX_WITHHELD_REPAYMENT = 'Foreign Tax Withheld Repayment',
  DRAW = 'Draw',
  PAYUP = 'Principal Payup',
  CASH_BALANCE_FIELD_ADJUSTMENT = 'Cash Balance Field Adjustment',
  UNSUPPORTED = 'UNSUPPORTED',
}

export type TransactionTypeKey = keyof typeof TransactionTypeEnum

export const transactionTypeKeys = Object.keys(
  TransactionTypeEnum,
) as TransactionTypeKey[]

export const CASH_ENTRIES = [
  TransactionTypeEnum.CASH_TRANSFER,
  TransactionTypeEnum.COUPON,
  TransactionTypeEnum.CUSTODY_FEE,
  TransactionTypeEnum.EQUITY_DIVIDEND,
  TransactionTypeEnum.MANAGEMENT_FEE,
  TransactionTypeEnum.MMF_PAYMENT,
  TransactionTypeEnum.OTHER_EXPENSE,
  TransactionTypeEnum.OTHER_INCOME,
  TransactionTypeEnum.OVERDRAFT_CHARGE,
  TransactionTypeEnum.PAYDOWN,
  TransactionTypeEnum.SECURITIES_LENDING_INCOME,
  TransactionTypeEnum.CAPITALIZATION_OF_INTEREST,
  TransactionTypeEnum.MARGIN_VARIATION_IN,
  TransactionTypeEnum.MARGIN_VARIATION_OUT,
  TransactionTypeEnum.RETURN_OF_CAPITAL,
  TransactionTypeEnum.CAPITAL_GAIN_DISTRIBUTION,
  TransactionTypeEnum.CAPITAL_CONTRIBUTION,
  TransactionTypeEnum.FOREIGN_TAX_WITHHELD,
  TransactionTypeEnum.FOREIGN_TAX_WITHHELD_REPAYMENT,
  TransactionTypeEnum.CONTRIBUTION_WITHDRAWAL,
  TransactionTypeEnum.CASH_FROM_CORPORATE_ACTION,
  TransactionTypeEnum.CASH_RECEIVABLE_ON_LOAD,
  TransactionTypeEnum.PREPAYMENT_ACCELERATION_PREMIUM,
  TransactionTypeEnum.MUTUAL_FUND_DIVIDEND,
  TransactionTypeEnum.PAYUP,
  TransactionTypeEnum.CASH_BALANCE_FIELD_ADJUSTMENT,
]

export const TRADE_ENTRIES = [
  TransactionTypeEnum.BUY,
  TransactionTypeEnum.CALL_REDEMPTION,
  TransactionTypeEnum.CORPORATE_ACTION_SELL,
  TransactionTypeEnum.MATURITY,
  TransactionTypeEnum.PUT_REDEMPTION,
  TransactionTypeEnum.REDEMPTION,
  TransactionTypeEnum.SELL,
  TransactionTypeEnum.OPTION_SETTLE,
  TransactionTypeEnum.FUTURE_SETTLE,
  TransactionTypeEnum.OPTION_WRITE,
  TransactionTypeEnum.SHORT_SELL,
  TransactionTypeEnum.EXPIRATION,
  TransactionTypeEnum.BUY_COVER,
  TransactionTypeEnum.PRINCIPAL_REPAYMENT,
  TransactionTypeEnum.OPTION_EXERCISE,
  TransactionTypeEnum.DRAW,
]

export const FX_ENTRIES = [
  TransactionTypeEnum.FX_SPOT_TRADE,
  TransactionTypeEnum.FX_FORWARD_TRADE,
]

export const DIRECT_TRANSFER = [TransactionTypeEnum.DIRECT_CASH_TRANSFER]

export const TRANSFERS = [
  TransactionTypeEnum.TRANSFER_OUT,
  TransactionTypeEnum.TRANSFER_IN,
  TransactionTypeEnum.EXCHANGE_IN,
  TransactionTypeEnum.EXCHANGE_OUT,
  TransactionTypeEnum.INITIAL_LOAD,
]

export const TransactionTypes: Record<TransactionTypeEnum, TransactionType> = {
  [TransactionTypeEnum.CASH_TRANSFER]: {
    id: 1,
    name: TransactionTypeEnum.CASH_TRANSFER,
    transactionTypeKey: 'CASH_TRANSFER',
    code: 'TRN',
  },
  [TransactionTypeEnum.COUPON]: {
    id: 3,
    name: TransactionTypeEnum.COUPON,
    transactionTypeKey: 'COUPON',
    code: 'CPN',
  },
  [TransactionTypeEnum.MANAGEMENT_FEE]: {
    id: 5,
    name: TransactionTypeEnum.MANAGEMENT_FEE,
    transactionTypeKey: 'MANAGEMENT_FEE',
    code: 'MEXP',
  },
  [TransactionTypeEnum.CUSTODY_FEE]: {
    id: 6,
    name: TransactionTypeEnum.CUSTODY_FEE,
    transactionTypeKey: 'CUSTODY_FEE',
    code: 'CEXP',
  },
  [TransactionTypeEnum.MMF_PAYMENT]: {
    id: 7,
    name: TransactionTypeEnum.MMF_PAYMENT,
    transactionTypeKey: 'MMF_PAYMENT',
    code: 'MMF',
  },
  [TransactionTypeEnum.PAYDOWN]: {
    id: 8,
    name: TransactionTypeEnum.PAYDOWN,
    transactionTypeKey: 'PAYDOWN',
    code: 'PPD',
  },
  [TransactionTypeEnum.EQUITY_DIVIDEND]: {
    id: 9,
    name: TransactionTypeEnum.EQUITY_DIVIDEND,
    transactionTypeKey: 'EQUITY_DIVIDEND',
    code: 'DVD',
  },
  [TransactionTypeEnum.SECURITIES_LENDING_INCOME]: {
    id: 10,
    name: TransactionTypeEnum.SECURITIES_LENDING_INCOME,
    transactionTypeKey: 'SECURITIES_LENDING_INCOME',
    code: 'SLI',
  },
  [TransactionTypeEnum.OVERDRAFT_CHARGE]: {
    id: 11,
    name: TransactionTypeEnum.OVERDRAFT_CHARGE,
    transactionTypeKey: 'OVERDRAFT_CHARGE',
    code: 'OCH',
  },
  [TransactionTypeEnum.BUY]: {
    id: 12,
    name: TransactionTypeEnum.BUY,
    transactionTypeKey: 'BUY',
    code: 'BUY',
  },
  [TransactionTypeEnum.SELL]: {
    id: 13,
    name: TransactionTypeEnum.SELL,

    transactionTypeKey: 'SELL',
    code: 'SELL',
  },
  [TransactionTypeEnum.MATURITY]: {
    id: 14,
    name: TransactionTypeEnum.MATURITY,
    transactionTypeKey: 'MATURITY',
    code: 'MTY',
  },
  [TransactionTypeEnum.REDEMPTION]: {
    id: 15,
    name: TransactionTypeEnum.REDEMPTION,
    transactionTypeKey: 'REDEMPTION',
    code: 'RDM',
  },
  [TransactionTypeEnum.TRANSFER_IN]: {
    id: 16,
    name: TransactionTypeEnum.TRANSFER_IN,
    transactionTypeKey: 'TRANSFER_IN',
    code: 'TRNI',
  },
  [TransactionTypeEnum.TRANSFER_OUT]: {
    id: 17,
    name: TransactionTypeEnum.TRANSFER_OUT,
    transactionTypeKey: 'TRANSFER_OUT',
    code: 'TRNO',
  },
  [TransactionTypeEnum.CALL_REDEMPTION]: {
    id: 18,
    name: TransactionTypeEnum.CALL_REDEMPTION,
    transactionTypeKey: 'CALL_REDEMPTION',
    code: 'CRDM',
  },
  [TransactionTypeEnum.PUT_REDEMPTION]: {
    id: 19,
    name: TransactionTypeEnum.PUT_REDEMPTION,
    transactionTypeKey: 'PUT_REDEMPTION',
    code: 'PRDM',
  },
  [TransactionTypeEnum.OTHER_INCOME]: {
    id: 20,
    name: TransactionTypeEnum.OTHER_INCOME,
    transactionTypeKey: 'OTHER_INCOME',
    code: 'INC',
  },
  [TransactionTypeEnum.FX_TRADE]: {
    id: 21,
    name: TransactionTypeEnum.FX_TRADE,
    transactionTypeKey: 'FX_TRADE',
    code: 'FXT',
  },
  [TransactionTypeEnum.OTHER_EXPENSE]: {
    id: 22,
    name: TransactionTypeEnum.OTHER_EXPENSE,
    transactionTypeKey: 'OTHER_EXPENSE',
    code: 'EXP',
  },
  [TransactionTypeEnum.FX_SPOT_TRADE]: {
    id: 25,
    name: TransactionTypeEnum.FX_SPOT_TRADE,
    transactionTypeKey: 'FX_SPOT_TRADE',
    code: 'FXS',
  },
  [TransactionTypeEnum.FX_FORWARD_TRADE]: {
    id: 26,
    name: TransactionTypeEnum.FX_FORWARD_TRADE,
    transactionTypeKey: 'FX_FORWARD_TRADE',
    code: 'FXF',
  },
  [TransactionTypeEnum.WRITE_DOWN]: {
    id: 27,
    name: TransactionTypeEnum.WRITE_DOWN,
    transactionTypeKey: 'WRITE_DOWN',
    code: 'WD',
  },
  [TransactionTypeEnum.CORPORATE_ACTION]: {
    id: 28,
    name: TransactionTypeEnum.CORPORATE_ACTION,
    transactionTypeKey: 'CORPORATE_ACTION',
    code: 'CACT',
  },
  [TransactionTypeEnum.EXCHANGE_IN]: {
    id: 29,
    name: TransactionTypeEnum.EXCHANGE_IN,
    transactionTypeKey: 'EXCHANGE_IN',
    code: 'EXIN',
  },
  [TransactionTypeEnum.EXCHANGE_OUT]: {
    id: 30,
    name: TransactionTypeEnum.EXCHANGE_OUT,
    transactionTypeKey: 'EXCHANGE_OUT',
    code: 'EXOT',
  },
  [TransactionTypeEnum.CORPORATE_ACTION_SELL]: {
    id: 31,
    name: TransactionTypeEnum.CORPORATE_ACTION_SELL,
    transactionTypeKey: 'CORPORATE_ACTION_SELL',
    code: 'CASL',
  },
  [TransactionTypeEnum.INITIAL_LOAD]: {
    id: 32,
    name: TransactionTypeEnum.INITIAL_LOAD,
    transactionTypeKey: 'INITIAL_LOAD',
    code: 'LOAD',
  },
  [TransactionTypeEnum.CAPITALIZATION_OF_INTEREST]: {
    id: 33,
    name: TransactionTypeEnum.CAPITALIZATION_OF_INTEREST,
    transactionTypeKey: 'CAPITALIZATION_OF_INTEREST',
    code: 'CAPI',
  },
  [TransactionTypeEnum.EXPIRATION]: {
    id: 35,
    name: TransactionTypeEnum.EXPIRATION,
    transactionTypeKey: 'EXPIRATION',
    code: 'EXPR',
  },
  [TransactionTypeEnum.OPTION_SETTLE]: {
    id: 36,
    name: TransactionTypeEnum.OPTION_SETTLE,
    transactionTypeKey: 'OPTION_SETTLE',
    code: 'OPST',
  },
  [TransactionTypeEnum.OPTION_WRITE]: {
    id: 37,
    name: TransactionTypeEnum.OPTION_WRITE,
    transactionTypeKey: 'OPTION_WRITE',
    code: 'OWRI',
  },
  [TransactionTypeEnum.BUY_COVER]: {
    id: 38,
    name: TransactionTypeEnum.BUY_COVER,
    transactionTypeKey: 'BUY_COVER',
    code: 'BUYC',
  },
  [TransactionTypeEnum.SHORT_SELL]: {
    id: 39,
    name: TransactionTypeEnum.SHORT_SELL,
    transactionTypeKey: 'SHORT_SELL',
    code: 'SHSL',
  },
  [TransactionTypeEnum.MARGIN_VARIATION_IN]: {
    id: 40,
    name: TransactionTypeEnum.MARGIN_VARIATION_IN,
    transactionTypeKey: 'MARGIN_VARIATION_IN',
    code: 'MVI',
  },
  [TransactionTypeEnum.MARGIN_VARIATION_OUT]: {
    id: 41,
    name: TransactionTypeEnum.MARGIN_VARIATION_OUT,
    transactionTypeKey: 'MARGIN_VARIATION_OUT',
    code: 'MVO',
  },
  [TransactionTypeEnum.FUTURE_SETTLE]: {
    id: 42,
    name: TransactionTypeEnum.FUTURE_SETTLE,
    transactionTypeKey: 'FUTURE_SETTLE',
    code: 'FTST',
  },
  [TransactionTypeEnum.RETURN_OF_CAPITAL]: {
    id: 43,
    name: TransactionTypeEnum.RETURN_OF_CAPITAL,
    transactionTypeKey: 'RETURN_OF_CAPITAL',
    code: 'RCAP',
  },
  [TransactionTypeEnum.CAPITAL_GAIN_DISTRIBUTION]: {
    id: 44,
    name: TransactionTypeEnum.CAPITAL_GAIN_DISTRIBUTION,
    transactionTypeKey: 'CAPITAL_GAIN_DISTRIBUTION',
    code: 'CAPD',
  },
  [TransactionTypeEnum.CAPITAL_CONTRIBUTION]: {
    id: 45,
    name: TransactionTypeEnum.CAPITAL_CONTRIBUTION,
    transactionTypeKey: 'CAPITAL_CONTRIBUTION',
    code: 'CAPC',
  },
  [TransactionTypeEnum.FOREIGN_TAX_WITHHELD]: {
    id: 46,
    name: TransactionTypeEnum.FOREIGN_TAX_WITHHELD,
    transactionTypeKey: 'FOREIGN_TAX_WITHHELD',
    code: 'FTW',
  },
  [TransactionTypeEnum.INITIAL_LOAD_OF_DEFERRED]: {
    id: 47,
    name: TransactionTypeEnum.INITIAL_LOAD_OF_DEFERRED,
    transactionTypeKey: 'INITIAL_LOAD_OF_DEFERRED',
    code: 'ILOD',
  },
  [TransactionTypeEnum.CONTRIBUTION_WITHDRAWAL]: {
    id: 48,
    name: TransactionTypeEnum.CONTRIBUTION_WITHDRAWAL,
    transactionTypeKey: 'CONTRIBUTION_WITHDRAWAL',
    code: 'CCW',
  },
  [TransactionTypeEnum.CASH_FROM_CORPORATE_ACTION]: {
    id: 49,
    name: TransactionTypeEnum.CASH_FROM_CORPORATE_ACTION,
    transactionTypeKey: 'CASH_FROM_CORPORATE_ACTION',
    code: 'CFCA',
  },
  [TransactionTypeEnum.CASH_RECEIVABLE_ON_LOAD]: {
    id: 50,
    name: TransactionTypeEnum.CASH_RECEIVABLE_ON_LOAD,
    transactionTypeKey: 'CASH_RECEIVABLE_ON_LOAD',
    code: 'CROL',
  },
  [TransactionTypeEnum.DIRECT_CASH_TRANSFER]: {
    id: 51,
    name: TransactionTypeEnum.DIRECT_CASH_TRANSFER,
    transactionTypeKey: 'DIRECT_CASH_TRANSFER',
    code: 'WIRE',
  },
  [TransactionTypeEnum.STOCK_SPLIT]: {
    id: 52,
    name: TransactionTypeEnum.STOCK_SPLIT,
    transactionTypeKey: 'STOCK_SPLIT',
    code: 'SPLT',
  },
  [TransactionTypeEnum.CURRENCY_FORWARD]: {
    id: 53,
    name: TransactionTypeEnum.CURRENCY_FORWARD,
    transactionTypeKey: 'CURRENCY_FORWARD',
    code: 'FWD',
  },
  [TransactionTypeEnum.PRINCIPAL_REPAYMENT]: {
    id: 56,
    name: TransactionTypeEnum.PRINCIPAL_REPAYMENT,
    transactionTypeKey: 'PRINCIPAL_REPAYMENT',
    code: 'RPMT',
  },
  [TransactionTypeEnum.OPTION_EXERCISE]: {
    id: 57,
    name: TransactionTypeEnum.OPTION_EXERCISE,
    transactionTypeKey: 'OPTION_EXERCISE',
    code: 'OPEX',
  },
  [TransactionTypeEnum.PREPAYMENT_ACCELERATION_PREMIUM]: {
    id: 58,
    name: TransactionTypeEnum.PREPAYMENT_ACCELERATION_PREMIUM,
    transactionTypeKey: 'PREPAYMENT_ACCELERATION_PREMIUM',
    code: 'PPAP',
  },
  [TransactionTypeEnum.MUTUAL_FUND_DIVIDEND]: {
    id: 59,
    name: TransactionTypeEnum.MUTUAL_FUND_DIVIDEND,
    transactionTypeKey: 'MUTUAL_FUND_DIVIDEND',
    code: 'MFD',
  },
  [TransactionTypeEnum.FOREIGN_TAX_WITHHELD_REPAYMENT]: {
    id: 60,
    name: TransactionTypeEnum.FOREIGN_TAX_WITHHELD_REPAYMENT,
    transactionTypeKey: 'FOREIGN_TAX_WITHHELD_REPAYMENT',
    code: 'FTWR',
  },
  [TransactionTypeEnum.DRAW]: {
    id: 61,
    name: TransactionTypeEnum.DRAW,
    transactionTypeKey: 'DRAW',
    code: 'DRAW',
  },
  [TransactionTypeEnum.PAYUP]: {
    id: 62,
    name: TransactionTypeEnum.PAYUP,
    transactionTypeKey: 'PAYUP',
    code: 'PPU',
  },
  [TransactionTypeEnum.CASH_BALANCE_FIELD_ADJUSTMENT]: {
    id: 63,
    name: TransactionTypeEnum.CASH_BALANCE_FIELD_ADJUSTMENT,
    transactionTypeKey: 'CASH_BALANCE_FIELD_ADJUSTMENT',
    code: 'CBFA',
  },
  [TransactionTypeEnum.UNSUPPORTED]: {
    id: -1,
    name: TransactionTypeEnum.UNSUPPORTED,
    transactionTypeKey: 'UNSUPPORTED',
    code: 'UNSUPPORTED',
  },
}

export const transactionTypesVal: TransactionType[] =
  Object.values(TransactionTypes)

/** Placeholder transaction type used when the API returns an unsupported transaction type */
export const UnsupportedTransactionType = {
  id: -1,
  name: TransactionTypeEnum.UNSUPPORTED,
  code: 'UNSUPPORTED',
}

export const ENTRIES: TransactionTypeEnum[] = [
  TransactionTypeEnum.BUY,
  TransactionTypeEnum.TRANSFER_IN,
  TransactionTypeEnum.EXCHANGE_IN,
  TransactionTypeEnum.INITIAL_LOAD,
  TransactionTypeEnum.SHORT_SELL,
  TransactionTypeEnum.OPTION_WRITE,
  TransactionTypeEnum.OPTION_EXERCISE,
  TransactionTypeEnum.CURRENCY_FORWARD,
]
export const EXITS: TransactionTypeEnum[] = [
  TransactionTypeEnum.SELL,
  TransactionTypeEnum.TRANSFER_OUT,
  TransactionTypeEnum.EXCHANGE_OUT,
  TransactionTypeEnum.BUY_COVER,
  TransactionTypeEnum.EXPIRATION,
  TransactionTypeEnum.OPTION_SETTLE,
  TransactionTypeEnum.FUTURE_SETTLE,
  TransactionTypeEnum.MATURITY,
  TransactionTypeEnum.REDEMPTION,
  TransactionTypeEnum.CALL_REDEMPTION,
  TransactionTypeEnum.PUT_REDEMPTION,
  TransactionTypeEnum.CORPORATE_ACTION_SELL,
  TransactionTypeEnum.PRINCIPAL_REPAYMENT,
  TransactionTypeEnum.OPTION_EXERCISE,
  TransactionTypeEnum.CURRENCY_FORWARD,
]

export const SECURITY_TRANSFER_IN: TransactionTypeEnum[] = [
  TransactionTypeEnum.TRANSFER_IN,
  TransactionTypeEnum.EXCHANGE_IN,
]

export const SECURITY_TRANSFER_OUT: TransactionTypeEnum[] = [
  TransactionTypeEnum.TRANSFER_OUT,
  TransactionTypeEnum.EXCHANGE_OUT,
]

export const VALID_INVENTORY_TYPE_MAP: Map<TransactionTypeEnum, InventoryType> =
  new Map()
    .set(TransactionTypeEnum.BUY, InventoryType.LONG)
    .set(TransactionTypeEnum.SELL, InventoryType.LONG)
    .set(TransactionTypeEnum.OPTION_WRITE, InventoryType.ISSUE)
    .set(TransactionTypeEnum.BUY_COVER, InventoryType.SHORT)
    .set(TransactionTypeEnum.SHORT_SELL, InventoryType.SHORT)
